import { Alert, Button, Container, Group, Stack, Tabs, Text, Title } from '@mantine/core';
import { notify } from '@/lib/notify';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Practitioner } from '@medplum/fhirtypes';
import { IconPlus, IconUser, IconUsers } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useClients } from '../../contexts/ClientsContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { fetchAdminDirectory } from '../../lib/adminDirectoryService';
import { ADMIN_EXTENSION_URLS } from '../../lib/adminDirectoryConstants';
import { organizationIdFromPractitioner } from '../../lib/createFulfilledEpisodePair';
import { canRoleAddClient } from '../../lib/staffAddClientPermissions';
import { StaffAddClientModal } from '../users/components/StaffAddClientModal';
import { STAFF_ROLES, type Practice } from '../users/types/rbac';
import { ClientsTab } from './tabs/ClientsTab';
import { CaseloadContactsPage } from './CaseloadContactsPage';
import { resolveCaseloadActiveTab } from './caseloadTabs';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

function profilePracticeId(profile: ReturnType<typeof useMedplumProfile>): string | undefined {
  if (profile?.resourceType !== 'Practitioner' || !profile.id) return undefined;
  const pract = profile as Practitioner;
  const fromExt = organizationIdFromPractitioner(pract);
  if (fromExt) return fromExt;
  const extOrg = pract.extension?.find((e) => e.url === ADMIN_EXTENSION_URLS.organization)
    ?.valueReference?.reference;
  return extOrg?.startsWith('Organization/') ? extOrg.replace('Organization/', '') : undefined;
}

function isPlatformStaffRole(role: (typeof STAFF_ROLES)[number]): boolean {
  return role === 'unify-admin';
}

/**
 * Caseload management screen
 * Displays client caseload with various status tabs
 */
export function ClientsScreen(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const location = useLocation();
  const { role } = usePermissions();
  const { clients, clientGroups, caseloadError, caseloadLoading, reloadCaseload } = useClients();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveCaseloadActiveTab(searchParams.get('tab'));
  const canAddClient = canRoleAddClient(role);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [practices, setPractices] = useState<Practice[]>([]);
  const [scopePractice, setScopePractice] = useState<Practice | undefined>();
  const [addClientLoading, setAddClientLoading] = useState(false);

  useEffect(() => {
    reloadCaseload();
  }, [location.pathname, activeTab, reloadCaseload]);

  const handleOpenAddClient = useCallback(async () => {
    setAddClientLoading(true);
    try {
      const directory = await fetchAdminDirectory(medplum);
      setPractices(directory.practices);
      const orgId = profilePracticeId(profile);
      if (!isPlatformStaffRole(role) && orgId) {
        setScopePractice(directory.practices.find((p) => p.id === orgId));
      } else {
        setScopePractice(undefined);
      }
      setShowAddClientModal(true);
    } catch (err) {
      console.error('[Caseload] load practices for add client failed:', err);
      notify({
        type: 'error',
        title: 'Could not open add client',
        error: err,
        message: 'Please try again.',
      });
    } finally {
      setAddClientLoading(false);
    }
  }, [medplum, profile, role]);

  const practicesForModal = scopePractice != null ? [scopePractice] : practices;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <div>
            <Title order={2}>Caseload</Title>
            <Text c="dimmed" size="sm">
              Manage your client caseload and track client status
            </Text>
          </div>
          {canAddClient ? (
            <Button
              leftSection={<IconPlus size={16} />}
              loading={addClientLoading}
              onClick={() => void handleOpenAddClient()}
            >
              Add Client
            </Button>
          ) : null}
        </Group>

        {caseloadError && (
          <Alert color="red" title="Could not load caseload from Medplum">
            {getUserFacingErrorMessage(caseloadError)}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(value) => {
            if (!value || value === 'clients') {
              setSearchParams({}, { replace: true });
              return;
            }
            setSearchParams({ tab: value }, { replace: true });
          }}
        >
          <Tabs.List styles={{ list: { borderBottom: 'none' } }}>
            <Tabs.Tab value="clients" leftSection={<IconUser size={16} />} style={{ fontWeight: 400 }}>
              Clients
            </Tabs.Tab>
            <Tabs.Tab value="contacts" leftSection={<IconUsers size={16} />} style={{ fontWeight: 400 }}>
              Contacts
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="clients" mt="md">
            <ClientsTab clients={clients} clientGroups={clientGroups} loading={caseloadLoading} />
          </Tabs.Panel>

          <Tabs.Panel value="contacts" mt="md">
            <CaseloadContactsPage
              clients={clients}
              clientGroups={clientGroups}
              caseloadLoading={caseloadLoading}
            />
          </Tabs.Panel>
        </Tabs>

        {showAddClientModal ? (
          <StaffAddClientModal
            opened
            onClose={() => {
              setShowAddClientModal(false);
              reloadCaseload();
            }}
            practice={scopePractice}
            practices={practicesForModal}
          />
        ) : null}
      </Stack>
    </Container>
  );
}
